import { BaseDialog } from '@airship-platform/jam-web/components';
import { zodResolver } from '@hookform/resolvers/zod';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import {
  AppointmentProposal,
  useAppointmentProposals,
} from '../../../data-access/appointmentProposals';
import ROUTE_PATHS from '../../../routes/paths';

const endServiceCallSchema = z.object({
  endServiceCallNotes: z.string().optional(),
});

type EndServiceCallInput = z.infer<typeof endServiceCallSchema>;

const defaultValues: EndServiceCallInput = {
  endServiceCallNotes: '',
};

interface EndServiceCallButtonProps {
  proposal: AppointmentProposal;
}

const EndServiceCallButton: React.FC<EndServiceCallButtonProps> = ({
  proposal,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { updateAppointmentProposal, isLoading, error } =
    useAppointmentProposals();
  const navigate = useNavigate();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<EndServiceCallInput>({
    defaultValues,
    resolver: zodResolver(endServiceCallSchema),
  });

  const onSubmit = async (data: EndServiceCallInput) => {
    await updateAppointmentProposal(
      proposal.appointmentId,
      proposal.appointmentProposalId,
      {
        ...proposal,
        status: 'FINISHED',
        finishNotes: data.endServiceCallNotes,
      }
    );
    setIsDialogOpen(false);
    !error && navigate(ROUTE_PATHS.SCHEDULE_DETAIL(proposal.appointmentId));
  };

  return (
    <>
      <Button
        onClick={() => setIsDialogOpen(true)}
        size="large"
        color="softRed"
        fullWidth
      >
        End service call
      </Button>

      <BaseDialog
        open={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        title="End Service Call"
        footerAction={{
          left: {
            children: 'Cancel',
            onClick: () => setIsDialogOpen(false),
            color: 'softGold',
          },
          right: {
            children: 'Confirm and end',
            type: 'submit',
            form: 'end-service-form',
            loading: isLoading,
            disabled: isLoading,
          },
        }}
      >
        <Box
          id="end-service-form"
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        >
          <Typography variant="body2">
            Once you end your service call in Airship, you will be redirected to
            ServiceTitan to close out. If you've built your proposal, all
            options will be synced in ServiceTitan. Additionally, you can
            include an optional note to explain the reason for ending your
            service call.
          </Typography>
          <Controller
            name="endServiceCallNotes"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="add note (optional)"
                error={!!errors.endServiceCallNotes}
                helperText={errors.endServiceCallNotes?.message}
                multiline
                rows={2}
                variant="outlined"
                fullWidth
              />
            )}
          />
        </Box>
      </BaseDialog>
    </>
  );
};

export default EndServiceCallButton;
